import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { env } from "configs/EnvironmentConfig";
import { jwtDecode } from "jwt-decode";
import { performFullLogout } from "utils/logoutUtils";
// import { AUTH_TOKEN } from "constants/AuthConstant";
import { SEC_AUTH_M_TK } from "constants/AuthConstant";

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const decoded = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch (error) {
    console.error("Token validation error:", error);
    return true;
  }
};

// Custom baseQuery with better error handling
const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
  const result = await fetchBaseQuery({
    baseUrl: env.BASE_URL,
    prepareHeaders: (headers, { getState, endpoint }) => {
      headers.set("ngrok-skip-browser-warning", "69420");
      if (endpoint !== undefined) {
        // document-microservice web-crawler expects a static bearer, not the login JWT
        const webCrawlerEndpoints = new Set([
          "checkWebCrawlerHealth",
          "discoverSitemap",
          "scrapeUrlsSession",
          "scrapeUrlsDirect",
          "generatePdfSession",
          "generatePdfDirect",
          "downloadPdf",
        ]);
        if (webCrawlerEndpoints.has(endpoint)) {
          headers.set(
            "Authorization",
            "Bearer V29yayBpcyBsaWZlIGFuZCBsaWZlIGlzIHdvcmsuIExpZmUgaXMgYWJzdHJhY3QgYW5kIHdvcmsgaXMgZGlzY3JldGUuIFdvcmsgaGFyZCB0byBtYWtlIGxpZmUgZGlzY3JldGUu"
          );
          return headers;
        }

        // const token = localStorage.getItem(AUTH_TOKEN);
        const token = localStorage.getItem(SEC_AUTH_M_TK);

        if (token) {
          if (isTokenExpired(token)) {
            // Handle token expiration with comprehensive cleanup
            // console.log("Token expired, performing full logout and redirecting");
            performFullLogout();
            window.location.href = "/auth/login";

            throw new Error("Token expired");
          }
          headers.set("Authorization", `Bearer ${token}`);
        }
      }
      // headers.set("Content-Type", "application/json");
      return headers;
    },
  })(args, api, extraOptions);

  // Handle network errors
  if (result.error) {
    // Check if it's a network error (no response)
    if (result.error.status === 'FETCH_ERROR' || result.error.status === 'PARSING_ERROR') {
      console.error('Network Error in RTK Query:', {
        error: result.error,
        endpoint: args?.url || 'unknown',
        baseURL: env.BASE_URL,
      });
      
      // Return a more descriptive error
     // createApi() creates a central API manager for your app.
     //It handles:API calls,Caching,Auto refetch,Optimistic updates,Error handling,Tag invalidation

      return {
        ...result,
        error: {
          ...result.error,
          message: result.error.error || result.error.message || 'Network Error: Unable to connect to server',
          isNetworkError: true,
        },
      };
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithErrorHandling,
  // Add tag types for better cache management
  tagTypes: ['UserContext', 'UserContextFiles', 'KBNames', 'KBFiles', 'searchQuestionOnContext', 'Files', 'UserSummary', 'UsageLimits', 'ChatAgents', 'UNAUTHORIZED', 'ContactSupport', 'Tools', 'AvailableTools', 'OutboundOTP', 'AuditLogs', 'Users'],
  // Keep cache data for 5 minutes by default
  keepUnusedDataFor: 300,
  endpoints: (builder) => ({
    refetchOnReconnect: true,
    refetchOnFocus: true,
    fetchUsers: builder.query({
      refetchOnFocus: true,
      // query: () => "/users",
      query: () => "/KBnames",
      transformResponse: (res) => {
        
        return res;
      },
      providesTags: (userId) => [{ type: "UserContext" }],
    }),
    fetchUserFiles: builder.query({
      refetchOnReconnect: true,
      refetchOnFocus: true,
      query: (userId) => ({
        url: "/get_files",
        method: "POST",
        body: { contextId: userId },
      }),
      transformResponse: (res) => {
        
        return res || [];
      },
      providesTags: (result, error, userId) => [
        { type: "UserContextFiles", id: userId },
      ],
    }),
    //make modification to the api.
    flushUserData: builder.mutation({
      query: (userId) => ({
        // url: "/flush_user_data",
        url: "/delete-KB-from-vectorDB",
        method: "POST",
        body: { contextId: userId },
      }),
      onQueryStarted: async (userId, { dispatch, queryFulfilled }) => {
        
        
        const patchResult = dispatch(
          apiSlice.util.updateQueryData("fetchUsers", undefined, (draft) => {
            
            
            // Handle different possible data structures
            let usersArray = null;
            
            if (draft && draft.users && Array.isArray(draft.users)) {
              usersArray = draft.users;
            } else if (Array.isArray(draft)) {
              usersArray = draft;
            } else if (draft && draft.kbnames && Array.isArray(draft.kbnames)) {
              usersArray = draft.kbnames;
            }
            
            if (usersArray) {
              const index = usersArray.indexOf(userId);

              if (index !== -1) {
                usersArray.splice(index, 1);
                
                console.warn("🔍 User not found in draft:", userId);
              }
            } else {
              console.warn("🔍 Invalid draft structure:", draft);
            }
          })
        );
        
        try {
          const { data } = await queryFulfilled;
          
        } catch (error) {
          
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error) => {
        // Only invalidate cache on success to avoid interfering with optimistic update undo
        return error ? [] : [{ type: "UserContext" }];
      },
    }),
    deleteFile: builder.mutation({
      query: ({ contextId, fileName }) => ({
        url: "/delete_embedding_and_metadata",
        method: "DELETE",
        body: { contextId, file_name: fileName },
      }),
      onQueryStarted: async (
        { contextId, fileName },
        { dispatch, queryFulfilled }
      ) => {
        // Optimistic update
        const patchResult = dispatch(
          apiSlice.util.updateQueryData(
            "fetchUserFiles",
            contextId,
            (draft) => {
              if (Array.isArray(draft.files)) {
                // Use indexOf for direct string comparison
                const index = draft.files.indexOf(fileName);

                if (index !== -1) {
                  draft.files.splice(index, 1);
                } else {
                  console.warn("File not found:", fileName);
                }
                
              } else {
                
              }
            }
          )
        );
        try {
          const { data } = await queryFulfilled; // Wait for server response
        } catch (error) {
          // Undo the optimistic update if the request fails
          patchResult.undo();
        }
      },
      // invalidatesTags: ["UserContextFiles"],
    }),
    search: builder.query({
      query: ({ userId, message, call_sid ,caller_number,language}) => ({
        url: "/search",
        method: "POST",
        body: { context_id: userId, message, call_sid ,caller_number,language},
      }),
      transformResponse: (res) => {
        return res || "";
      },
      providesTags: [{ type: "searchQuestionOnContext" }],
    }),
    getAllKbNames: builder.query({
      query: () => ({
        url: "/getallkbnames",
        method: "GET",
      }),
    }),
    uploadFile: builder.mutation({
      query: ({ contextId, comments, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("kb_name", contextId);
        formData.append("comments", comments);
        return {
          url: "/upload_file",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contextId }) => [
        { type: "Files", id: contextId },
      ],
    }),
    updateFeedback: builder.mutation({
      query: ({ id, feedback }) => ({
        url: `/update_feedback/${id}`,
        method: "PUT",
        body: { feedback },
      }),
    }),
    
    // Usage tracking endpoint
    fetchUsageLimitsAndCounts: builder.query({
      query: () => ({
        url: "/max_limit_and_count",
        method: "GET",
      }),
      transformResponse: (res) => {
        
        return res;
      },
      
      keepUnusedDataFor: 60, // 1 minute
      providesTags: ['UsageLimits'],
    }),

    // Approved tools list with caching
    getApprovedTools: builder.query({
      query: (status = 'Approved') => ({
        url: '/tool/GetToolRequestWithStatus',
        method: 'GET',
        params: { status },
      }),
      transformResponse: (res) => Array.isArray(res) ? res : [],
      providesTags: ['Tools'],
      refetchOnFocus: true,
      refetchOnReconnect: true,
      keepUnusedDataFor: 300,
    }),

    // Available tools list with caching
    getAvailableTools: builder.query({
      query: () => ({
        url: '/tool/available-tools',
        method: 'GET',
      }),
      transformResponse: (res) => Array.isArray(res) ? res : [],
      providesTags: ['AvailableTools'],
      refetchOnFocus: true,
      refetchOnReconnect: true,
      keepUnusedDataFor: 300,
    }),
    fetchAuditLogs: builder.query({
      query: ({ tenantId, page = 1, pageSize = 10 , search, start_date, end_date}) => {
        const params = new URLSearchParams({
          tenant_id: tenantId,
          page: page.toString(),
          page_size: pageSize.toString(),
          start_date : start_date,
          end_date : end_date,
          search: search

        });
        return {
          url: `/audit-logs?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response) => {
        return response || { audit_logs: [], total_count: 0 };
      },
      providesTags: ['AuditLogs'],
      refetchOnFocus: true,
      refetchOnReconnect: true,
      keepUnusedDataFor: 300,
    }),

  }),
});

export const {
  useFetchUsersQuery,
  useFetchUserFilesQuery,
  useLazySearchQuery,
  useUploadFileMutation,
  useFlushUserDataMutation,
  useDeleteFileMutation,
  useGetAllKbNamesQuery,
  useUpdateFeedbackMutation,
  useFetchUsageLimitsAndCountsQuery,
  useLazyFetchUsageLimitsAndCountsQuery,
  useGetApprovedToolsQuery,
  useGetAvailableToolsQuery,
  useFetchAuditLogsQuery,
} = apiSlice;

// Utility function to reset the entire API cache
export const resetApiCache = (dispatch) => {
  // Reset all cached data in the API slice
  dispatch(apiSlice.util.resetApiState());

  // Invalidate all tags to force refetch on next access
  dispatch(apiSlice.util.invalidateTags(['UserContext', 'UserContextFiles', 'KBNames', 'KBFiles', 'searchQuestionOnContext', 'Files', 'UserSummary']));

  
};
